import { useMemo } from 'react';
import { Spreadsheet } from '../../spreadsheet';
import { useModels } from '../../../lib/supabase/models/supabaseModelService';
import { Card, CardContent } from '../../ui/card';
import { DateTime } from 'luxon';

interface ModelStatisticsSpreadsheetRow {
  A: string; // Category
  B: string; // Normal Count
  C: string; // Unlock Count
  D: string; // Total Count
}

interface ModelStatisticsSpreadsheetProps {
  defaultMonth?: number;
  defaultYear?: number;
  isFullscreen?: boolean;
}

export function ModelStatisticsSpreadsheet({ defaultMonth, defaultYear, isFullscreen }: ModelStatisticsSpreadsheetProps) {
  const monthNumber = defaultMonth !== undefined ? defaultMonth : DateTime.utc().month;
  const yearNumber = defaultYear !== undefined ? defaultYear : DateTime.utc().year;

  // Fetch models data
  const { data: modelsData, isLoading: modelsLoading } = useModels();

  // Deep memoization for stable references
  const models = useMemo(() => {
    return modelsData || [];
  }, [JSON.stringify(modelsData)]);

  // Calculate model statistics
  const modelStats = useMemo(() => {
    const stats = {
      uk: { normal: 0, unlock: 0 },
      fp: { normal: 0, unlock: 0 },
      ukSet1: { normal: 0, unlock: 0 },
      ukSet2: { normal: 0, unlock: 0 },
      fpSet1: { normal: 0, unlock: 0 },
      fpSet2: { normal: 0, unlock: 0 }
    };

    models.forEach(model => {
      const isUK = model.pay_class === 'UK';
      const isFP = model.pay_class === 'FP';
      const isNormal = model.account_type === 'Normal';
      const isUnlock = model.account_type === 'Unlock';
      const set = model.set;

      if (model.paused) return;

      // Count by country
      if (isUK) {
        if (isNormal) stats.uk.normal++;
        if (isUnlock) stats.uk.unlock++;
      } else if (isFP) {
        if (isNormal) stats.fp.normal++;
        if (isUnlock) stats.fp.unlock++;
      }

      // Count by country and set
      if (isUK && set === 1) {
        if (isNormal) stats.ukSet1.normal++;
        if (isUnlock) stats.ukSet1.unlock++;
      } else if (isUK && set === 2) {
        if (isNormal) stats.ukSet2.normal++;
        if (isUnlock) stats.ukSet2.unlock++;
      } else if (isFP && set === 1) {
        if (isNormal) stats.fpSet1.normal++;
        if (isUnlock) stats.fpSet1.unlock++;
      } else if (isFP && set === 2) {
        if (isNormal) stats.fpSet2.normal++;
        if (isUnlock) stats.fpSet2.unlock++;
      }
    });

    return stats;
  }, [models]);

  // Process data for spreadsheet
  const getSpreadsheetData = () => {
    const rows: ModelStatisticsSpreadsheetRow[] = [];

    // Add UK models section
    rows.push({
      A: 'UK Models',
      B: modelStats.uk.normal.toString(),
      C: modelStats.uk.unlock.toString(),
      D: (modelStats.uk.normal + modelStats.uk.unlock).toString()
    });

    // Add FP models section
    rows.push({
      A: 'FP Models',
      B: modelStats.fp.normal.toString(),
      C: modelStats.fp.unlock.toString(),
      D: (modelStats.fp.normal + modelStats.fp.unlock).toString()
    });

    // Add empty row
    rows.push({
      A: '',
      B: '',
      C: '',
      D: ''
    });

    // Add UK Set breakdown
    rows.push({
      A: 'UK Set 1 Models',
      B: modelStats.ukSet1.normal.toString(),
      C: modelStats.ukSet1.unlock.toString(),
      D: (modelStats.ukSet1.normal + modelStats.ukSet1.unlock).toString()
    });

    rows.push({
      A: 'UK Set 2 Models',
      B: modelStats.ukSet2.normal.toString(),
      C: modelStats.ukSet2.unlock.toString(),
      D: (modelStats.ukSet2.normal + modelStats.ukSet2.unlock).toString()
    });

    // Add empty row
    rows.push({
      A: '',
      B: '',
      C: '',
      D: ''
    });

    // Add FP Set breakdown
    rows.push({
      A: 'FP Set 1 Models',
      B: modelStats.fpSet1.normal.toString(),
      C: modelStats.fpSet1.unlock.toString(),
      D: (modelStats.fpSet1.normal + modelStats.fpSet1.unlock).toString()
    });

    rows.push({
      A: 'FP Set 2 Models',
      B: modelStats.fpSet2.normal.toString(),
      C: modelStats.fpSet2.unlock.toString(),
      D: (modelStats.fpSet2.normal + modelStats.fpSet2.unlock).toString()
    });

    // Add empty row
    rows.push({
      A: '',
      B: '',
      C: '',
      D: ''
    });

    // Add grand totals
    const totalNormal = modelStats.uk.normal + modelStats.fp.normal;
    const totalUnlock = modelStats.uk.unlock + modelStats.fp.unlock;
    
    rows.push({
      A: 'TOTAL MODELS',
      B: totalNormal.toString(),
      C: totalUnlock.toString(),
      D: (totalNormal + totalUnlock).toString()
    });

    return rows;
  };

  // Memoize the processed spreadsheet data
  const processedSpreadsheetData = useMemo(() => {
    return getSpreadsheetData();
  }, [modelStats]);

  // Pre-calculate row indices for styling
  const { totalRowIndex, emptyRowIndices } = useMemo(() => {
    const dataLength = processedSpreadsheetData.length;
    return {
      totalRowIndex: dataLength - 1,
      emptyRowIndices: [2, 5, 8] // Empty rows for spacing
    };
  }, [processedSpreadsheetData]);

  if (modelsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading model statistics...</div>
      </div>
    );
  }

  if (models.length === 0) {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    return (
      <div className="text-center py-8 text-muted-foreground">
        No model data found for {months[monthNumber]} {yearNumber}
      </div>
    );
  }

  // Custom column headers
  const columnHeaders = ['CATEGORY', 'NORMAL', 'UNLOCK', 'TOTAL'];

  // Cell styling function
  const getCellStyle = (rowIdx: number, colKey: string, _: any) => {
    const isTotalRow = rowIdx === totalRowIndex;
    const isEmptyRow = emptyRowIndices.includes(rowIdx);

    // Style empty rows
    if (isEmptyRow) {
      return {
        backgroundColor: '#f9fafb',
        borderBottom: '1px solid #e5e7eb'
      };
    }

    // Style totals row
    if (isTotalRow) {
      if (colKey === 'A') { // TOTAL label
        return {
          fontWeight: 700,
          backgroundColor: '#f3f4f6',
          borderTop: '2px solid #374151',
          fontSize: '14px'
        };
      } else { // Count columns
        return {
          fontWeight: 700,
          textAlign: 'center' as const,
          backgroundColor: '#f3f4f6',
          borderTop: '2px solid #374151',
          color: '#ef4444',
          fontSize: '14px'
        };
      }
    }

    // Style the CATEGORY column (column A)
    if (colKey === 'A' && !isTotalRow && !isEmptyRow) {
      return {
        fontWeight: 600,
        fontSize: '13px'
      };
    }

    // Style count columns (B, C, D)
    if (['B', 'C', 'D'].includes(colKey) && !isTotalRow && !isEmptyRow) {
      return {
        textAlign: 'center' as const,
        fontWeight: 500,
        fontSize: '13px'
      };
    }

    // Default styling
    return {
      fontSize: '12px'
    };
  };

  return (
    <div className="space-y-4">
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          <Spreadsheet
            columns={4}
            minRows={Math.max(processedSpreadsheetData.length, 20)}
            data={processedSpreadsheetData}
            editable={false}
            columnWidths={[250, 100, 100, 100]}
            rowHeight={35}
            showColumnHeaders={true}
            customColumnHeaders={columnHeaders}
            fillHeight={true}
            isFullscreen={isFullscreen}
            buffers={{ fullscreen: 65, normal: 65 }}
            getCellStyle={getCellStyle}
            csvFilename={`model-statistics-${yearNumber}-${(monthNumber + 1).toString().padStart(2, '0')}.csv`}
            csvIncludeHeaders={true}
          />
        </CardContent>
      </Card>
    </div>
  );
}

export default ModelStatisticsSpreadsheet; 